import { environmentStorageKey } from "@/lib/appEnvironment"
import { useAuthStore } from "@/store/authStore"
import { useWorkspaceStore } from "@/store/workspaceStore"

const pending = new Map<string, Promise<unknown>>()
const activeCounts = new Map<string, number>()
let sessionGeneration = 0
let workspaceGeneration = 0
let observingSession = false
function observeSession() {
  if (observingSession) return
  observingSession = true
  useAuthStore.subscribe((state, previous) => {
    if (state.user?.id !== previous.user?.id) sessionGeneration++
  })
  useWorkspaceStore.subscribe((state, previous) => {
    if (state.active?.id !== previous.active?.id) workspaceGeneration++
  })
}

export function captureSession(userId = useAuthStore.getState().user?.id, workspaceId?: string): () => void {
  observeSession()
  const generation = sessionGeneration
  const workspaceVersion = workspaceGeneration
  const check = () => {
    if (!userId || useAuthStore.getState().user?.id !== userId || generation !== sessionGeneration)
      throw new Error("A sessão mudou. O trabalho foi mantido para o dono original.")
    if (workspaceId && (workspaceId !== useWorkspaceStore.getState().active?.id || workspaceVersion !== workspaceGeneration))
      throw new Error("O workspace mudou. O trabalho foi mantido no escopo original.")
  }
  check()
  return check
}

/** Web Locks serialize tabs; the promise chain also covers WebViews without Web Locks. */
export async function withSubmissionLock<T>(key: string, operation: () => Promise<T>): Promise<T> {
  const name = environmentStorageKey(`submission-lock:${key}`)
  if (typeof navigator !== "undefined" && navigator.locks)
    return navigator.locks.request(name, operation)
  const next = (pending.get(name) ?? Promise.resolve()).catch(() => undefined).then(operation)
  pending.set(name, next)
  try { return await next } finally { if (pending.get(name) === next) pending.delete(name) }
}

/** Keep another tab's background worker from synchronizing or expiring an open draft. */
export function holdDraftActivity(key: string): () => void {
  activeCounts.set(key, (activeCounts.get(key) ?? 0) + 1)
  let resolveActive = () => undefined as void
  let released = false
  const active = new Promise<void>(resolve => { resolveActive = resolve })
  if (typeof navigator !== "undefined" && navigator.locks) {
    void navigator.locks.request(environmentStorageKey(`draft-active:${key}`), { mode: "shared" }, () => active)
      .catch(error => console.warn("Draft activity lock unavailable; this tab retains its local activity guard", error))
  }
  return () => {
    if (released) return
    released = true
    const count = (activeCounts.get(key) ?? 1) - 1
    if (count) activeCounts.set(key, count)
    else activeCounts.delete(key)
    resolveActive()
  }
}

const editors = new Set<string>()
export async function claimDraftEditor(key: string): Promise<() => void> {
  if (editors.has(key)) throw new Error("Este rascunho já está aberto em outra janela.")
  editors.add(key)
  if (typeof navigator === "undefined" || !navigator.locks) return () => { editors.delete(key) }
  return new Promise((resolve, reject) => {
    void navigator.locks.request(environmentStorageKey(`draft-editor:${key}`), { ifAvailable: true }, lock => {
      if (!lock) { editors.delete(key); reject(new Error("Este rascunho já está aberto em outra aba.")); return }
      return new Promise<void>(release => resolve(() => { editors.delete(key); release() }))
    }).catch(error => { editors.delete(key); reject(error) })
  })
}

export async function acquirePreviewLease(key: string): Promise<() => void> {
  const preview = `preview:${key}`
  if (typeof navigator === "undefined" || !navigator.locks) return holdDraftActivity(preview)
  return new Promise((resolve, reject) => {
    void navigator.locks.request(environmentStorageKey(`draft-active:${preview}`), { mode: "shared" }, () =>
      new Promise<void>(release => resolve(release))).catch(reject)
  })
}

export async function withInactiveDraft<T>(key: string, operation: () => Promise<T>): Promise<T | undefined> {
  if (activeCounts.has(key)) return undefined
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(environmentStorageKey(`draft-active:${key}`), { ifAvailable: true }, lock => lock && !activeCounts.has(key) ? operation() : undefined)
  }
  return operation()
}
